import { Fragment, useEffect, useState, type ReactElement } from "react";
import { Alert, Badge, Button, Icon } from "../../ui";
import type { BadgeTone } from "../../ui";
import { useBreakpoint } from "../../lib/useBreakpoint";
import { CredentialRow } from "./CredentialRow";
import { ScopeTree } from "./ScopeTree";
import { GenerateLeadDialog } from "./GenerateLeadDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import { selectionFromScopes } from "./scopes";
import { SANDBOX_BASE_URL, useSandboxesStore } from "./store";
import { formatCredits, formatLastUsed, formatTimestamp } from "./format";
import type { Sandbox, SandboxStatus } from "./types";
import styles from "./SandboxCard.module.css";

const TONE: Record<SandboxStatus, BadgeTone> = {
  creating: "warning",
  pending: "warning",
  active: "success",
  failed: "danger",
  expired: "neutral",
};

const LABEL: Record<SandboxStatus, string> = {
  creating: "Creating",
  pending: "Creating",
  active: "Active",
  failed: "Failed",
  expired: "Expired",
};

function Pending({ label }: { label: string }): ReactElement {
  return (
    <div className={styles.stat}>
      <span className={styles.statName}>{label}</span>
      <span className={styles.pending} aria-label={`Loading ${label}`}>
        &hellip;
      </span>
    </div>
  );
}

function Stat({ name, value }: { name: string; value: string }): ReactElement {
  return (
    <div className={styles.stat}>
      <span className={styles.statName}>{name}</span>
      <span className={styles.statValue}>{value}</span>
    </div>
  );
}

export function SandboxCard({ sandbox }: { sandbox: Sandbox }): ReactElement {
  const { isMobile } = useBreakpoint();
  const [expanded, setExpanded] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();

  const keys = useSandboxesStore((s) => s.keys[sandbox.id]);
  const scopeGroups = useSandboxesStore((s) => s.scopeGroups);
  const keyStatus = useSandboxesStore((s) => s.keyStatus[sandbox.id]);
  const loadKeys = useSandboxesStore((s) => s.loadKeys);
  const remove = useSandboxesStore((s) => s.remove);

  const provisioning =
    sandbox.status === "creating" || sandbox.status === "pending";
  const expandable = !provisioning && sandbox.status !== "failed";

  // A new card exists before the next list read prefetches it.
  useEffect(() => {
    if (provisioning || sandbox.status === "failed") return;
    void loadKeys(sandbox.id);
  }, [provisioning, sandbox.status, loadKeys, sandbox.id]);

  async function confirmDelete() {
    setDeleting(true);
    setDeleteError(undefined);
    try {
      await remove(sandbox.id);
      setDeleteOpen(false);
    } catch {
      setDeleteError("Could not delete this sandbox. Try again in a moment.");
    } finally {
      setDeleting(false);
    }
  }

  const keyList = keys ?? [];
  // A sandbox that never provisioned reports a balance it never had.
  const creditsKnown = sandbox.status !== "failed" && sandbox.credits !== null;

  return (
    <article
      className={[
        styles.card,
        provisioning ? styles.provisioning : "",
        sandbox.status === "failed" ? styles.failed : "",
        sandbox.status === "expired" ? styles.expired : "",
        expanded ? styles.open : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.head}>
        {expandable && !isMobile ? (
          <button
            type="button"
            className={styles.chevron}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse" : "Expand"}
            onClick={() => setExpanded((state) => !state)}
          >
            <Icon
              name="chevron"
              size={18}
              className={expanded ? styles.chevronOpen : undefined}
            />
          </button>
        ) : null}

        <h2 className={styles.name}>{sandbox.name}</h2>

        <Badge tone={TONE[sandbox.status]} shape="pill">
          {provisioning ? <span className={styles.spinner} aria-hidden /> : null}
          {LABEL[sandbox.status]}
        </Badge>

        {isMobile ? null : (
          <div className={styles.actions}>
            {sandbox.status === "active" ? (
              <Button size="sm" onClick={() => setLeadOpen(true)}>
                Generate lead
              </Button>
            ) : null}
            {provisioning ? null : (
              <button
                type="button"
                className={styles.iconAction}
                aria-label={`Delete ${sandbox.name}`}
                onClick={() => setDeleteOpen(true)}
              >
                <Icon name="trash" size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      {provisioning ? (
        <p className={styles.note}>
          Provisioning usually takes about a minute. This page refreshes on its
          own — the API key and secret appear as soon as it is ready.
        </p>
      ) : (
        <div className={styles.strip}>
          <Stat name="Expires" value={formatTimestamp(sandbox.expiresAt)} />
          {sandbox.status === "failed" ? (
            <>
              <Stat name="Credits" value="—" />
              {isMobile ? null : <Stat name="Key last used" value="—" />}
            </>
          ) : (
            <>
              <Stat name="Credits" value={formatCredits(sandbox.credits)} />
              {isMobile ? null : (
                <Stat
                  name="Key last used"
                  value={formatLastUsed(sandbox.lastUsedKnown, sandbox.lastUsedAt)}
                />
              )}
            </>
          )}
          {isMobile ? null : (
            <Stat name="Country" value={sandbox.country.toUpperCase()} />
          )}
        </div>
      )}

      {sandbox.status === "failed" ? (
        <p className={styles.failedNote}>
          Provisioning did not complete. Nothing was charged — delete this and
          create a new sandbox.
        </p>
      ) : null}

      {sandbox.status === "expired" ? (
        <p className={styles.expiredNote}>
          Expired {formatTimestamp(sandbox.expiresAt)} · this key is expired.
          Create a new sandbox to carry on testing.
        </p>
      ) : null}

      {expanded ? (
        <div className={styles.body}>
          {keyStatus === "error" ? (
            <Alert tone="danger" action={
              <Button size="sm" variant="secondary" onClick={() => void loadKeys(sandbox.id)}>
                Retry
              </Button>
            }>
              Could not load the credentials for this sandbox.
            </Alert>
          ) : null}

          {sandbox.lastUsedKnown && sandbox.lastUsedAt === null ? (
            <Alert tone="info">
              This key has never been used. Make one authenticated call to
              confirm your setup — Key last used updates as soon as it lands.
            </Alert>
          ) : null}

          <div className={styles.fields}>
            <CredentialRow label="Base URL" value={SANDBOX_BASE_URL} />
            {keyList.length ? (
              keyList.map((item, index) => (
                <Fragment key={item.id}>
                  <CredentialRow
                    label={
                      keyList.length > 1 ? `API Key ${index + 1}` : "API Key"
                    }
                    value={item.apiKey}
                    secret
                  />
                  <CredentialRow
                    label={
                      keyList.length > 1
                        ? `API Secret ${index + 1}`
                        : "API Secret"
                    }
                    value={item.apiSecret}
                    secret
                  />
                </Fragment>
              ))
            ) : keyStatus === "error" ? null : (
              <>
                <Pending label="API Key" />
                <Pending label="API Secret" />
              </>
            )}
            <Stat name="Created" value={formatTimestamp(sandbox.createdAt)} />
            <Stat name="Expiry date" value={formatTimestamp(sandbox.expiresAt)} />
            {isMobile ? (
              <Stat
                name="Key last used"
                value={formatLastUsed(sandbox.lastUsedKnown, sandbox.lastUsedAt)}
              />
            ) : null}
            <div className={styles.stat}>
              <span className={styles.statName}>Remaining credits</span>
              <span className={styles.statValue}>
                {formatCredits(sandbox.credits)}
              </span>
            </div>
          </div>

          {!keyList.length && keyStatus !== "error" ? (
            <div className={styles.scopes}>
              <h3 className={styles.scopesTitle}>Granted scopes</h3>
              <span className={styles.pending} aria-label="Loading granted scopes">
                &hellip;
              </span>
            </div>
          ) : null}

          {scopeGroups
            ? keyList.map((item, index) => (
                <div className={styles.scopes} key={item.id}>
                  <div className={styles.scopesHead}>
                    <h3 className={styles.scopesTitle}>
                      {keyList.length > 1
                        ? `Granted scopes · key ${index + 1}`
                        : "Granted scopes"}
                    </h3>
                    {item.scopes?.length ? (
                      <span className={styles.scopesCount}>
                        {scopeGroups.length - item.scopes.length} groups not
                        granted
                      </span>
                    ) : null}
                  </div>
                  {item.scopes === null || item.scopes === undefined ? (
                    <p className={styles.scopesNote}>
                      Could not read the granted scopes.
                    </p>
                  ) : item.scopes.length === 0 ? (
                    <p className={styles.scopesNote}>No scopes are granted.</p>
                  ) : (
                    <ScopeTree
                      mode="read"
                      groups={scopeGroups}
                      selection={selectionFromScopes(scopeGroups, item.scopes)}
                    />
                  )}
                </div>
              ))
            : null}

          {isMobile ? (
            <Button
              variant="dangerOutline"
              fullWidth
              onClick={() => setDeleteOpen(true)}
            >
              Delete sandbox
            </Button>
          ) : null}
        </div>
      ) : null}

      {isMobile && !provisioning ? (
        <div
          className={`${styles.mobileActions} ${expanded ? styles.stuck : ""}`}
        >
          {sandbox.status === "active" ? (
            <Button onClick={() => setLeadOpen(true)}>Generate lead</Button>
          ) : null}
          {expandable ? (
            <Button
              variant="secondary"
              aria-expanded={expanded}
              iconRight={
                <Icon
                  name="chevronDown"
                  size={16}
                  className={expanded ? styles.chevronUp : undefined}
                />
              }
              onClick={() => setExpanded((state) => !state)}
            >
              {expanded ? "Hide" : "Details"}
            </Button>
          ) : null}
          {expandable ? null : (
            <Button variant="secondary" onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          )}
        </div>
      ) : null}

      <GenerateLeadDialog
        sandbox={sandbox}
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={`Delete ${sandbox.name}?`}
        confirmLabel="Delete sandbox"
        phrase={sandbox.name}
        phraseLabel={`Type ${sandbox.name} to confirm`}
        placeholder={sandbox.name}
        mismatchHint="That does not match yet. Copy the name exactly, including hyphens."
        busy={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleteOpen(false)}
      >
        <p>
          The API key and secret stop working immediately — any integration still
          pointing at this sandbox starts failing authentication.
        </p>
        <p>
          Everything created inside it is destroyed: seeded users, seeded
          listings, generated leads and webhook subscriptions.
        </p>
        <p>
          {creditsKnown
            ? `The remaining ${formatCredits(sandbox.credits)} credits are lost.`
            : "Any remaining credits are lost."}{" "}
          A new sandbox starts from a fresh balance.
        </p>
        <p>
          This cannot be undone. Point your integration at another sandbox first,
          and copy anything you still need from the request logs on your side.
        </p>
      </ConfirmDialog>
    </article>
  );
}
